import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { FaArrowLeft, FaCompass, FaExternalLinkAlt, FaMusic, FaSpinner, FaTrophy, FaUsers } from 'react-icons/fa';
import { GiPianoKeys } from 'react-icons/gi';
import { addDoc, collection, getDocs, limit, query, serverTimestamp, where } from 'firebase/firestore';
import { db } from '../firebase';
import Cherry from '../Assets/Cherry.png';
import Mountain from '../Assets/JustMountain.png';
import Sky from '../Assets/Sky.png';
import SmallLogo from '../Assets/JapanLogo.png';
import JudgeFelipe from '../Assets/JudgeFelipe.png';
import JudgeEddy from '../Assets/JudgeEddyMarcano.webp';
import JudgeSusan from '../Assets/JudgeSusanSiman.jpg';

const COMPETITION_NAME = 'Simanof Japan Tour Competition 2026';
const COLLECTION_NAME = 'competitionApplications';

const PRIZES = [
  { place: '2nd Place', prize: '$700', detail: '10% off a 2026 Simanof tour', accent: 'border-red-300' },
  { place: '1st Place', prize: '$2,200', detail: 'Plus London Tour 2027 sponsorship + soloist feature', accent: 'border-red-600', featured: true },
  { place: '3rd Place', prize: '$100', detail: '10% off a 2026 Simanof tour', accent: 'border-red-300' },
];

const PURPOSE_SYMBOLS = [FaCompass, FaUsers, FaMusic, FaTrophy];

const REHEARSALS = [
  {
    id: 'august-4',
    label: 'August 4 rehearsal',
    date: 'August 4, 2026',
    time: '9:00 AM-5:00 PM',
    venue: 'J:COM Urayasu Hall - Harmony Hall',
    address: 'J:COM Urayasu Hall, Urayasu, Chiba, Japan',
    mapUrl: 'https://www.google.com/maps/search/?api=1&query=J%3ACOM%20Urayasu%20Hall%20Urayasu%20Chiba%20Japan',
  },
  {
    id: 'august-5',
    label: 'August 5 rehearsal',
    date: 'August 5, 2026',
    time: '9:00 AM-12:00 PM',
    venue: 'Tower Hall Funabori',
    address: 'Tower Hall Funabori, Edogawa City, Tokyo, Japan',
    mapUrl: 'https://www.google.com/maps/search/?api=1&query=Tower%20Hall%20Funabori%20Tokyo%20Japan',
  },
];

const REHEARSAL_OPTIONS = [
  { value: 'August 4 rehearsal', label: 'August 4 rehearsal only' },
  { value: 'August 5 rehearsal', label: 'August 5 rehearsal only' },
  { value: 'Both rehearsals', label: 'Both rehearsals' },
];

const JUDGES = [
  {
    name: 'Felipe Gomez Perez',
    role: 'Musical Conductor',
    image: JudgeFelipe,
    highlights: [
      'International conductor and artistic leader with a career across orchestral and operatic work.',
      'Recognized for poetic intensity, structural clarity, and a vivid conducting style.',
      'Brings deep experience in cultural exchange and major international touring projects.',
    ],
  },
  {
    name: 'Eddy G. Marcano',
    role: 'Violinist & Musical Conductor',
    image: JudgeEddy,
    imageClass: 'object-top',
    highlights: [
      "One of Venezuela's distinguished violinists, conductors, educators, and cultural leaders.",
      'Built an international career through El Sistema and the Simon Bolivar Symphony Orchestra.',
      'Known for artistic excellence, orchestral leadership, and mentoring young musicians.',
    ],
  },
  {
    name: 'Susan Siman',
    role: 'Founder & Director, Siman Orchestral Foundation',
    image: JudgeSusan,
    highlights: [
      'Founder and director of the Siman Orchestral Foundation.',
      'Leads international music education, tours, scholarships, and youth performance opportunities.',
      'Champions discipline, community, and the transformative power of music for young artists.',
    ],
  },
];

const REPERTOIRE = Object.freeze({
  Violin: [
    { ageRange: [8, 10], title: 'Age Group 8-10 (Violin)', pieces: ['Chorus from Judas Maccabaeus - G. F. Handel', 'Musette - J. S. Bach', "Hunters' Chorus - C. M. von Weber"] },
    { ageRange: [11, 14], title: 'Age Group 11-14 (Violin)', pieces: ['Rieding - Violin Concerto No. 2, Op. 35', 'Seitz - Student Concerto No. 5', 'Vivaldi - Concerto in A Minor, RV 356, 1st Movement'] },
    { ageRange: [15, 20], title: 'Age Group 15-20 (Violin)', pieces: ['Bach - Concerto in A Minor, BWV 1041', 'Accolay - Violin Concerto No. 2 in A Minor', 'Mozart - Violin Concerto No. 3 in G Major, K.216'] },
    { ageRange: [21, Infinity], title: 'Age Group 21+ (Violin)', pieces: ['Bruch - Violin Concerto No. 1 in G Minor, Op. 26', 'Mendelssohn - Violin Concerto in E Minor, Op. 64', 'Saint-Saens - Violin Concerto No. 3 in B Minor, Op. 61'] },
  ],
  Viola: [
    { ageRange: [8, 10], title: 'Age Group 8-10 (Viola)', pieces: ['Oskar Rieding - Concerto in E Minor, Op. 35', 'Seitz - Concerto No. 5, 1st Movement', 'Telemann - Concerto in G, 1st Movement'] },
    { ageRange: [11, 14], title: 'Age Group 11-14 (Viola)', pieces: ['Sonata in G Minor - 4th Movement', 'Veracini - Gigue', 'Seitz - Concerto No. 3 in C Minor'] },
    { ageRange: [15, 20], title: 'Age Group 15-20 (Viola)', pieces: ['Hoffmeister - Viola Concerto in D Minor', 'Bach/Casadesus - Concerto for Viola in C Minor', 'Handel/Casadesus - Viola Concerto, 1st Movement'] },
    { ageRange: [21, Infinity], title: 'Age Group 21+ (Viola)', pieces: ["Vivaldi's Four Seasons for Viola", 'Stamitz - Viola Concerto in D Minor', 'Vanhal - Viola Concerto in C Major'] },
  ],
  Cello: [
    { ageRange: [8, 10], title: 'Age Group 8-10 (Cello)', pieces: ['F. Seitz - Concerto No. 5, 1st Movement', 'C. Webster - Scherzo', 'Vivaldi - Cello Concerto in C Major, RV 399'] },
    { ageRange: [11, 14], title: 'Age Group 11-14 (Cello)', pieces: ['J. B. Breval - Sonata in C Major, Allegro', 'Bach - Cello Suite No. 1 in G Major', 'Goltermann - Rondo: Allegro Molto'] },
    { ageRange: [15, 21], title: 'Age Group 15-21 (Cello)', pieces: ['Elgar - Cello Concerto', 'Haydn - Concerto No. 1 in C Major', 'Breval - Rondo from Concerto No. 2 in D Major'] },
    { ageRange: [21, Infinity], title: 'Age Group 21+ (Cello)', pieces: ['Dvorak - Cello Concerto', 'Schumann - Cello Concerto, Op. 129', 'Barber - Cello Concerto'] },
  ],
  Flute: [
    { ageRange: [8, 10], title: 'Age Group 8-10 (Flute)', pieces: ['Wilhelm Popp - Little Melody, Op. 384', 'Giuseppe Gariboldi - Etude Melodique, Op. 132 No. 1', 'Jean-Louis Tulou - Petite Etude'] },
    { ageRange: [11, 14], title: 'Age Group 11-14 (Flute)', pieces: ['Telemann - Sonata in F Major, I. Vivace', 'Taffanel - Andante Pastoral et Scherzettino', 'Bach - Sonata in C Major, BWV 1033'] },
    { ageRange: [15, 21], title: 'Age Group 15-21 (Flute)', pieces: ['Mozart - Flute Concerto in D Major, K. 313', 'C. P. E. Bach - Sonata in D Minor, H. 514', 'Gaubert - Nocturne et Allegro Scherzando'] },
    { ageRange: [21, Infinity], title: 'Age Group 21+ (Flute)', pieces: ['Poulenc - Sonata for Flute and Piano', 'Enesco - Cantabile et Presto', 'Reinecke - Concerto in D Major, Op. 283'] },
  ],
  Clarinet: [
    { ageRange: [8, 10], title: 'Age Group 8-10 (Clarinet)', pieces: ['Baermann - Vocalise, Op. 63 No. 3', 'Gustav Langenus - First Solo', 'James Rae - Easy Study No. 3'] },
    { ageRange: [11, 14], title: 'Age Group 11-14 (Clarinet)', pieces: ['Baermann - Clarinet Method, Book 3', 'Stamitz - Concerto No. 3 in B-flat Major', 'Kreisler - Liebesfreud, arranged for clarinet'] },
    { ageRange: [15, 21], title: 'Age Group 15-21 (Clarinet)', pieces: ['Weber - Concertino for Clarinet, Op. 26', 'Saint-Saens - Sonata for Clarinet and Piano, Op. 167', 'Jeanjean - Arabesques'] },
    { ageRange: [21, Infinity], title: 'Age Group 21+ (Clarinet)', pieces: ['Nielsen - Clarinet Concerto', 'Brahms - Clarinet Sonata in F Minor', 'Tomasi - Clarinet Concerto'] },
  ],
  Piano: [
    { ageRange: [8, 10], title: 'Age Group 8-10 (Piano)', pieces: ['Bach - Minuet in G Major, BWV Anh. 114', 'Beethoven - Ecossaise in G Major, WoO 23', 'Gurlitt - Album for the Young, Op. 140 No. 3'] },
    { ageRange: [11, 14], title: 'Age Group 11-14 (Piano)', pieces: ['Burgmuller - Arabesque, Op. 100 No. 2', 'Schumann - The Wild Horseman, Op. 68 No. 8', 'Kabalevsky - Clowns, Op. 39 No. 20'] },
    { ageRange: [15, 21], title: 'Age Group 15-21 (Piano)', pieces: ['Beethoven - Sonatina in G Major, Anh. 5 No. 1', 'Chopin - Waltz in A Minor, B. 150', 'Satie - Gymnopedie No. 1'] },
    { ageRange: [21, Infinity], title: 'Age Group 21+ (Piano)', pieces: ['Beethoven - Moonlight Sonata, Op. 27 No. 2', 'Chopin - Nocturne in E-flat Major, Op. 9 No. 2', 'Debussy - Clair de Lune'] },
  ],
  Trumpet: [
    { ageRange: [8, 10], title: 'Age Group 8-10 (Trumpet)', pieces: ['When the Saints Go Marching In', 'The Beautiful Snow - Arban Method', 'Arban - Carnival of Venice, excerpt'] },
    { ageRange: [11, 14], title: 'Age Group 11-14 (Trumpet)', pieces: ['Arban - Characteristic Study No. 1', 'Arban - Fantasie Brillante, excerpt', 'Haydn - Trumpet Concerto, 1st Movement'] },
    { ageRange: [15, 21], title: 'Age Group 15-21 (Trumpet)', pieces: ['Hummel - Trumpet Concerto, 1st Movement', 'Cascade', 'Arutunian - Trumpet Concerto'] },
    { ageRange: [21, Infinity], title: 'Age Group 21+ (Trumpet)', pieces: ['Bohme - Trumpet Concerto in F Minor', 'Tomasi - Trumpet Concerto', 'Jolivet - Concerto for Trumpet'] },
  ],
  'Double Bass': [
    { ageRange: [8, 10], title: 'Age Group 8-10 (Double Bass)', pieces: ['Gavotte - Suzuki Bass Book 3', 'Minuet No. 2 - Suzuki Bass Book 3', 'Dvorak - Humoresque, arranged for bass'] },
    { ageRange: [11, 15], title: 'Age Group 11-15 (Double Bass)', pieces: ['Tambourin - Suzuki Bass Book 5', 'Keyper - Romance and Rondo', 'Capuzzi - Double Bass Concerto in F Major'] },
    { ageRange: [16, 21], title: 'Age Group 16-21 (Double Bass)', pieces: ['Bottesini - Elegy', 'Hertl - Sonata for Double Bass and Piano', 'Gliere - Intermezzo and Tarantella'] },
    { ageRange: [21, Infinity], title: 'Age Group 21+ (Double Bass)', pieces: ['Bottesini - Tarantella', 'Koussevitzky - Double Bass Concerto', 'Fryba - Suite in the Olden Style'] },
  ],
});

const GRADING_CATEGORIES = [
  { id: 'orchestraConcert', points: 5 },
  { id: 'time', points: 5 },
  { id: 'technique', points: 5 },
  { id: 'soundQuality', points: 5 },
  { id: 'phrasing', points: 5 },
];

const CONCERT_PROGRAM = [
  {
    id: 'firstHalf',
    pieces: [
      { title: 'Sakura Sakura', composer: 'Japanese traditional song', leader: 'Choir only' },
      { title: 'A New World Festival for Multi-level String or Full Orchestra', composer: 'Antonin Dvorak', leader: 'Susan Siman' },
      { title: 'Danzon No. 2', composer: 'Arturo Marquez', leader: 'Yusuke Kimoto' },
    ],
  },
  {
    id: 'intermission',
    intermission: true,
    pieces: [],
  },
  {
    id: 'secondHalf',
    pieces: [
      { title: 'Piano Concerto No. 20 in D minor, K. 466: III. Allegro assai', composer: 'W. A. Mozart', leader: 'Soloist: Alexander Brown / Eddy Marcano' },
      { title: 'Trabajadores Agricolas, Danzas del Ballet La Estancia, Movimiento No. 1', composer: 'Alberto Ginastera', leader: 'Susan Siman' },
      { title: 'Marche Slave, Op. 31', composer: 'Pyotr Ilyich Tchaikovsky', leader: 'Eddy Marcano' },
      { title: 'Conga del Fuego Nuevo', composer: 'Arturo Marquez', leader: 'Felipe Gomez' },
      { title: 'Children Dream Straight Ahead', composer: 'Takafumi Kakizaki', leader: 'Yusuke Kimoto' },
      { title: 'Colombia Tierra Querida', composer: 'Luis Eduardo "Lucho" Bermudez', leader: 'Felipe Gomez' },
    ],
  },
];

const CONCERT_PARTICIPATION_OPTIONS = [
  { value: 'First half only', label: 'First half only', orchestraPoints: 'Up to 2.5 / 5 Orchestra Concert points' },
  { value: 'Full concert', label: 'Full concert', orchestraPoints: 'Up to 5 / 5 Orchestra Concert points' },
];

const MUSIC_FOLDERS = Object.freeze({
  Bassoon: [{ label: 'Bassoon', url: 'https://drive.google.com/drive/folders/18fc7igkI3lWFVpCTdhDVLJ4eb72A75U7?usp=sharing' }],
  Cello: [{ label: 'Cello', url: 'https://drive.google.com/drive/folders/10tVfh8CbziN4pVSCvPFQKTHiVvIcr0NL?usp=sharing' }],
  Clarinet: [{ label: 'Clarinet', url: 'https://drive.google.com/drive/folders/1iDBFge6cLDfQ1s2o6W9yg4d6sjFN6FXb?usp=sharing' }],
  'Double Bass': [{ label: 'Bass', url: 'https://drive.google.com/drive/folders/1kTBbMc50mXV6w10XecmoSCw1F8R4bvlK?usp=sharing' }],
  Flute: [{ label: 'Flute', url: 'https://drive.google.com/drive/folders/1pgp0RVeh5aHfDsmLUcE8iqwV0NnOtR6j?usp=sharing' }],
  'French Horn': [{ label: 'French Horn', url: 'https://drive.google.com/drive/folders/1lVGhA8hbnota6VzSSiRmdPuu9joA-o1K?usp=sharing' }],
  Oboe: [{ label: 'Oboe', url: 'https://drive.google.com/drive/folders/18JZ7KqNSkO49FsL0qu6HFjieCTBGl82z?usp=sharing' }],
  Percussion: [{ label: 'Percussion', url: 'https://drive.google.com/drive/folders/1W5qKaUYmoY3ZBXZC5sGG9CNXdcybEauY?usp=sharing' }],
  Piano: [{ label: 'Piano', url: 'https://drive.google.com/drive/folders/1O3N5gji_C_l-mOe8LApdwHmVjz_Cv0aX?usp=sharing' }],
  Trombone: [{ label: 'Trombone', url: 'https://drive.google.com/drive/folders/1galGPnzcLebUvxU-TsXqF2k6RgOxNMus?usp=sharing' }],
  Trumpet: [{ label: 'Trumpet', url: 'https://drive.google.com/drive/folders/1DROHwRSMfbL3CJ3sUNfJ0X9E-XTYh1RI?usp=sharing' }],
  Tuba: [{ label: 'Tuba', url: 'https://drive.google.com/drive/folders/18TQe8s6G2cbuMYoPgv39RTVFeNUPCHXW?usp=sharing' }],
  Viola: [{ label: 'Viola', url: 'https://drive.google.com/drive/folders/1e7xRqe77R7tWJQSQgyUWArpNweWwB-sr?usp=sharing' }],
  Violin: [
    { label: 'Violin 1', url: 'https://drive.google.com/drive/folders/1y3qB2TMPRxG7jaioPK1trJ7XB8MOyUMS?usp=sharing' },
    { label: 'Violin 2', url: 'https://drive.google.com/drive/folders/1jYCx6MnHMz0nO4mQvjm0XQSyFxmYnTiQ?usp=sharing' },
  ],
});

const ALL_INSTRUMENTS = Object.freeze(
  Array.from(new Set([...Object.keys(REPERTOIRE), ...Object.keys(MUSIC_FOLDERS)])).sort()
);

function getRepertoireGroup(instrument, age) {
  const groups = REPERTOIRE[instrument] || [];
  const numericAge = Number(age);

  if (!groups.length || Number.isNaN(numericAge)) return null;

  const minAge = groups[0].ageRange[0];
  if (numericAge < minAge) return { tooYoung: true, minAge };

  return groups.find((group) => numericAge >= group.ageRange[0] && numericAge <= group.ageRange[1]) || null;
}

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  age: '',
  instrument: '',
  chosenPiece: '',
  rehearsalAttendance: '',
  concertParticipation: '',
  willAttend: false,
};

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'ja', label: '日本語' },
];

const COPY = {
  en: {
    languageLabel: 'Language',
    home: 'Home',
    tourLabel: 'Japan Tour',
    heroEyebrow: 'Simanof Japan 2026',
    heroTitle: 'Grand Competition',
    heroText: 'Apply for the Japan Tour competition, review your age group repertoire, and prepare for a finalist opportunity connected to the 2026 Japan stage.',
    purposeEyebrow: 'Competition Purpose',
    purposeTitle: 'Created For Musicians Ready To Explore',
    purposeText: 'Simanof created this competition to open new opportunities for musicians who want to explore, grow, and experience an international stage. It is especially designed for students and young musicians who have never competed before.',
    purposeCards: [
      { title: 'A welcoming first competition', text: 'This competition is mainly for musicians who may be entering a competition for the first time. The goal is to make the experience serious, supportive, and inspiring.' },
      { title: 'Orchestra + solo assessment', text: 'We believe a musician should be heard both alone and with other musicians. That is why the overall score includes the orchestra performance and the solo competition.' },
      { title: 'Concerto piece required', text: 'To compete, each musician may choose any concerto piece, but the solo selection must be a concerto.' },
      { title: 'A path to London 2027', text: 'The winner receives $2,200 USD plus sponsorship for the Simanof London Tour 2027 and the opportunity to perform as a soloist.' },
    ],
    judgesEyebrow: 'Competition Panel',
    judgesTitle: 'Meet the Judges',
    judgesText: 'The panel brings together international conducting, orchestral performance, education, and Simanof Foundation leadership.',
    judges: {
      'Felipe Gomez Perez': {
        role: 'Musical Conductor',
        highlights: [
          'International conductor and artistic leader with a career across orchestral and operatic work.',
          'Recognized for poetic intensity, structural clarity, and a vivid conducting style.',
          'Brings deep experience in cultural exchange and major international touring projects.',
        ],
      },
      'Eddy G. Marcano': {
        role: 'Violinist & Musical Conductor',
        highlights: [
          "One of Venezuela's distinguished violinists, conductors, educators, and cultural leaders.",
          'Built an international career through El Sistema and the Simon Bolivar Symphony Orchestra.',
          'Known for artistic excellence, orchestral leadership, and mentoring young musicians.',
        ],
      },
      'Susan Siman': {
        role: 'Founder & Director, Siman Orchestral Foundation',
        highlights: [
          'Founder and director of the Siman Orchestral Foundation.',
          'Leads international music education, tours, scholarships, and youth performance opportunities.',
          'Champions discipline, community, and the transformative power of music for young artists.',
        ],
      },
    },
    processEyebrow: 'Participant Process',
    processTitle: 'Sign Up, Rehearse, Perform, Compete',
    processText: 'The process is simple: apply online, attend at least one rehearsal, perform in the August 6 concert, then compete on August 7.',
    requiredOrder: 'Required Order',
    orderOne: 'Sign up online.',
    orderTwo: 'Attend one rehearsal.',
    orderThree: 'Concert, then competition.',
    stepLabel: 'Step',
    processSteps: [
      { step: 'Sign up', date: 'Open now', time: 'Online application', title: 'Submit your competition form', venue: 'Choose your instrument, piece, and rehearsal attendance.', detail: 'After applying, download the concert music for your instrument and start preparing both your concert part and your competition piece.' },
      { step: 'Rehearse', date: 'August 4 or August 5, 2026', time: 'Attend at least one rehearsal', title: 'Join one required rehearsal', venue: 'August 4 at J:COM Urayasu Hall or August 5 at Tower Hall Funabori.', detail: 'Participants may attend one rehearsal or both. This prepares the orchestra for the August 6 concert.' },
      { step: 'Concert', date: 'August 6, 2026', time: 'Arrive by 3:30 PM', title: 'Perform in the Japan Tour concert', venue: 'Concert hall details to be announced.', detail: 'Concert participation is required before competing in the finals.' },
      { step: 'Compete', date: 'August 7, 2026', time: 'Arrive by 9:00 AM', title: 'Competition finals day', venue: 'Square Ebara, Shinagawa City, Tokyo.', detail: 'Eliminations run from 9:00 AM to 1:00 PM, followed by the masterclass and top 3 finalist concert.' },
    ],
    rehearsalEyebrow: 'Rehearsal Locations',
    rehearsalTitle: 'Where To Be For Rehearsal',
    rehearsalText: 'Applicants must choose at least one rehearsal in the application form. Both maps are provided so families can plan arrival.',
    translatorNote: 'A Japanese translator will be available during rehearsal for native Japanese speakers.',
    arrivalWindow: 'Arrival window',
    mapLocation: 'Map location',
    openMap: 'Open map',
    musicEyebrow: 'Concert Music',
    musicTitle: 'Download Your Concert Music',
    musicText: 'Every competitor also participates in the August 6 concert. Select your instrument below, open the Google Drive folder for your part, and bring the music to rehearsal.',
    musicHow: 'How To Get Your Music',
    musicSteps: ['Choose your instrument in the selector.', 'Open the matching Google Drive folder.', 'Download or print your part before rehearsal.'],
    gradingEyebrow: 'Scoring System',
    gradingTitle: 'How The Competition Is Graded',
    gradingText: 'Each competitor is evaluated across five categories. Each category is worth 5 points, for a total score of 25 points.',
    totalPoints: 'Total Points',
    pointsAbbrev: 'pts',
    gradingCategories: {
      orchestraConcert: { name: 'Orchestra Concert', detail: 'Participation, preparation, and stage responsibility during the August 6 concert. First half only earns up to 2.5 of these 5 points.' },
      time: { name: 'Time', detail: 'Steady pulse, rhythmic accuracy, and secure entrances.' },
      technique: { name: 'Technique', detail: 'Control, accuracy, articulation, and command of the instrument.' },
      soundQuality: { name: 'Sound Quality', detail: 'Tone, projection, balance, and musical color.' },
      phrasing: { name: 'Phrasing', detail: 'Musical shape, expression, dynamics, and style.' },
    },
    selectMusicInstrument: 'Select instrument for concert music',
    availableFolder: 'Available Folder',
    availableFolders: 'Available Folders',
    openMusic: 'Open',
    musicWord: 'Music',
    violinNote: 'Violin players should confirm whether they are assigned Violin 1 or Violin 2. If unsure, review both folders with your teacher.',
    concertEyebrow: 'August 6 Concert',
    piecesTitle: 'Pieces We Will Play',
    programTitle: 'Concert Program Order',
    programText: 'Choose the concert option that matches your preparation. First half only is for students who are not ready for Marche Slave yet, but it can only receive up to 2.5 of the 5 Orchestra Concert points. Full concert students can receive up to all 5 Orchestra Concert points.',
    programSections: {
      firstHalf: { section: 'First Half', note: 'Students may choose this option if they are not ready for Marche Slave yet.' },
      intermission: { section: 'Intermission' },
      secondHalf: { section: 'Second Half', note: 'Full concert students continue after intermission and prepare the complete program.' },
    },
    concertChoiceTitle: 'Choose Your Concert Level',
    firstHalfPoints: 'Up to 2.5 / 5 Orchestra Concert points',
    fullConcertPoints: 'Up to 5 / 5 Orchestra Concert points',
    firstHalfBadge: 'First half option',
    fullConcertBadge: 'Full concert option',
    arriveBy: 'Arrive by 3:30 PM',
    finalsEyebrow: 'August 7',
    finalsTitle: 'Competition Finals Day',
    finalsText: "Competitors only need to report to Square Ebara by 9:00 AM. The day includes eliminations, a break, Eddy Marcano's masterclass, the top 3 finalist concert, and the winner announcement at the venue.",
    venueLabel: 'Competition Venue',
    mustArrive: 'Competitors must arrive by 9:00 AM.',
    finals: [
      { time: '9:00 AM', title: 'Arrive at Square Ebara', location: 'Square Ebara, 4-5-28 Ebara, Shinagawa City, Tokyo 142-0063', detail: 'All competitors must be at the competition venue by 9:00 AM.' },
      { time: '9:00 AM-1:00 PM', title: 'Elimination round', location: 'Square Ebara', detail: 'Competition performances, evaluations, orchestra and ensemble categories, and adjudication sessions.' },
      { time: '1:00 PM-2:30 PM', title: 'Break', location: 'Square Ebara area', detail: 'Lunch and reset before the finalist session.' },
      { time: '2:45 PM-3:30 PM', title: 'Masterclass + top 3 finalist concert', location: 'Square Ebara', detail: 'Masterclass by Eddy Marcano, top 3 finalist performances, feedback, and winner selection at the venue.' },
    ],
    applicationEyebrow: 'Application',
    applyTitle: 'Apply',
    applyText: 'The repertoire guide updates automatically after you select an instrument and age.',
    success: 'Thank you. Your application was sent successfully. The Simanof team will email you with the next steps.',
    submitAnother: 'Submit another',
    form: { firstName: 'First name', lastName: 'Last name', email: 'Email', age: 'Age', instrument: 'Instrument', chooseInstrument: 'Choose your instrument', concertMusic: 'Concert orchestra music', musicComing: 'Concert music folder is coming soon.', chosenPiece: 'Chosen concerto piece for the competition', concertoMusicEmail: 'Before the competition, please email your concerto sheet music to Information@simanof.org so the judges have the music.', rehearsalAttendance: 'Which rehearsal will you attend?', chooseRehearsal: 'Choose rehearsal attendance', concertParticipation: 'Concert participation option', chooseConcertParticipation: 'Choose concert option', firstHalfLabel: 'First half only', fullConcertLabel: 'Full concert', firstHalfDescription: 'Play the first half only. This is for students who can perform the opening concert music but are not ready for Marche Slave yet. This option can receive up to 2.5 of the 5 Orchestra Concert points.', fullConcertDescription: 'Play the full concert. This includes the first half and all music after intermission, including Marche Slave. This option can receive up to all 5 Orchestra Concert points.', checkbox: 'I understand that competition participants must attend at least one rehearsal and the August 6 concert option selected above, arrive at the concert hall by 3:30 PM, and attend required competition events connected to the Japan Tour.', submit: 'Submit Application (No Fee)', submitting: 'Submitting...' },
    rehearsalOptions: ['August 4 rehearsal only', 'August 5 rehearsal only', 'Both rehearsals'],
  },
  es: {
    languageLabel: 'Idioma', home: 'Inicio', tourLabel: 'Gira Japón', heroEyebrow: 'Simanof Japón 2026', heroTitle: 'Gran Competencia', heroText: 'Inscríbete a la competencia de la Gira Japón, revisa el repertorio por edad y prepárate para una oportunidad como finalista en el escenario de Japón 2026.', purposeEyebrow: 'Propósito de la Competencia', purposeTitle: 'Creada Para Músicos Con Ganas De Explorar', purposeText: 'Simanof creó esta competencia para abrir nuevas oportunidades a músicos que quieren explorar, crecer y vivir una experiencia en un escenario internacional. Está especialmente pensada para estudiantes y jóvenes músicos que nunca han competido antes.', purposeCards: [{ title: 'Una primera competencia acogedora', text: 'Esta competencia está dirigida principalmente a músicos que quizás entran a una competencia por primera vez. La meta es que la experiencia sea seria, de apoyo e inspiradora.' }, { title: 'Evaluación orquestal + solista', text: 'Creemos que un músico debe ser escuchado tanto solo como tocando con otros músicos. Por eso el puntaje total incluye la presentación de orquesta y la competencia solista.' }, { title: 'Pieza de concierto requerida', text: 'Para competir, cada músico puede elegir cualquier concierto, pero la selección solista debe ser una pieza de concierto.' }, { title: 'Camino a Londres 2027', text: 'El ganador recibirá $2,200 USD más patrocinio para Simanof London Tour 2027 y la oportunidad de tocar como solista.' }], judgesEyebrow: 'Panel de Competencia', judgesTitle: 'Conoce a los Jueces', judgesText: 'El panel reúne dirección internacional, interpretación orquestal, educación y liderazgo de la Fundación Simanof.', judges: { 'Felipe Gomez Perez': { role: 'Director Musical', highlights: ['Director internacional y líder artístico con trayectoria en trabajo orquestal y operístico.', 'Reconocido por intensidad poética, claridad estructural y un estilo de dirección expresivo.', 'Aporta amplia experiencia en intercambio cultural y giras internacionales.'] }, 'Eddy G. Marcano': { role: 'Violinista y Director Musical', highlights: ['Uno de los violinistas, directores, educadores y líderes culturales destacados de Venezuela.', 'Desarrolló una carrera internacional a través de El Sistema y la Orquesta Sinfónica Simón Bolívar.', 'Reconocido por excelencia artística, liderazgo orquestal y mentoría de jóvenes músicos.'] }, 'Susan Siman': { role: 'Fundadora y Directora, Fundación Orquestal Siman', highlights: ['Fundadora y directora de la Fundación Orquestal Siman.', 'Lidera educación musical internacional, giras, becas y oportunidades de presentación para jóvenes.', 'Promueve disciplina, comunidad y el poder transformador de la música para jóvenes artistas.'] } }, processEyebrow: 'Proceso del Participante', processTitle: 'Inscríbete, Ensaya, Toca el Concierto, Compite', processText: 'El proceso es simple: inscríbete en línea, asiste al menos a un ensayo, participa en el concierto del 6 de agosto y compite el 7 de agosto.', requiredOrder: 'Orden Requerido', orderOne: 'Inscripción en línea.', orderTwo: 'Asiste a un ensayo.', orderThree: 'Concierto y luego competencia.', stepLabel: 'Paso',
    processSteps: [
      { step: 'Inscríbete', date: 'Abierto ahora', time: 'Aplicación en línea', title: 'Envía tu formulario de competencia', venue: 'Elige tu instrumento, pieza y asistencia al ensayo.', detail: 'Después de aplicar, descarga la música del concierto para tu instrumento y prepara tu parte de concierto y tu pieza de competencia.' },
      { step: 'Ensaya', date: '4 o 5 de agosto de 2026', time: 'Asiste al menos a un ensayo', title: 'Participa en un ensayo requerido', venue: '4 de agosto en J:COM Urayasu Hall o 5 de agosto en Tower Hall Funabori.', detail: 'Los participantes pueden asistir a un ensayo o a ambos. Esto prepara la orquesta para el concierto del 6 de agosto.' },
      { step: 'Concierto', date: '6 de agosto de 2026', time: 'Llegar a las 3:30 PM', title: 'Toca en el concierto de la Gira Japón', venue: 'Detalles del salón de concierto por anunciar.', detail: 'La participación en el concierto es requerida antes de competir en las finales.' },
      { step: 'Compite', date: '7 de agosto de 2026', time: 'Llegar a las 9:00 AM', title: 'Día de finales de competencia', venue: 'Square Ebara, Shinagawa City, Tokio.', detail: 'Las eliminatorias son de 9:00 AM a 1:00 PM, seguidas por la clase magistral y el concierto de los 3 finalistas.' },
    ],
    rehearsalEyebrow: 'Lugares de Ensayo', rehearsalTitle: 'Dónde Llegar Para el Ensayo', rehearsalText: 'Los aplicantes deben elegir al menos un ensayo en el formulario. Los mapas están incluidos para que las familias puedan planificar la llegada.', translatorNote: 'Habrá traductor japonés durante el ensayo para hablantes nativos de japonés.', arrivalWindow: 'Horario de llegada', mapLocation: 'Ubicación en mapa', openMap: 'Abrir mapa', musicEyebrow: 'Música del Concierto', musicTitle: 'Descarga Tu Música del Concierto', musicText: 'Cada competidor también participa en el concierto del 6 de agosto. Selecciona tu instrumento, abre la carpeta de Google Drive y lleva tu música al ensayo.', musicHow: 'Cómo Obtener Tu Música', musicSteps: ['Elige tu instrumento en el selector.', 'Abre la carpeta correspondiente en Google Drive.', 'Descarga o imprime tu parte antes del ensayo.'], gradingEyebrow: 'Sistema de Evaluación', gradingTitle: 'Cómo Se Califica La Competencia', gradingText: 'Cada competidor será evaluado en cinco categorías. Cada categoría vale 5 puntos, para un total de 25 puntos.', totalPoints: 'Puntos Totales', pointsAbbrev: 'pts', gradingCategories: { orchestraConcert: { name: 'Concierto de Orquesta', detail: 'Participación, preparación y responsabilidad escénica durante el concierto del 6 de agosto. Solo primera parte puede recibir hasta 2.5 de estos 5 puntos.' }, time: { name: 'Tiempo', detail: 'Pulso estable, precisión rítmica y entradas seguras.' }, technique: { name: 'Técnica', detail: 'Control, precisión, articulación y dominio del instrumento.' }, soundQuality: { name: 'Calidad de Sonido', detail: 'Tono, proyección, balance y color musical.' }, phrasing: { name: 'Fraseo', detail: 'Forma musical, expresión, dinámicas y estilo.' } }, selectMusicInstrument: 'Selecciona instrumento para la música del concierto', availableFolder: 'Carpeta Disponible', availableFolders: 'Carpetas Disponibles', openMusic: 'Abrir', musicWord: 'Música', violinNote: 'Los violinistas deben confirmar si están asignados a Violín 1 o Violín 2. Si no estás seguro, revisa ambas carpetas con tu maestro.', concertEyebrow: 'Concierto 6 de Agosto', piecesTitle: 'Piezas Que Vamos a Tocar', programTitle: 'Orden Del Programa', programText: 'Elige la opción de concierto según tu preparación. Solo primera parte es para estudiantes que todavía no están listos para Marcha Slave, pero solo puede recibir hasta 2.5 de los 5 puntos de Orquesta. El concierto completo puede recibir hasta los 5 puntos.', programSections: { firstHalf: { section: 'Primera Parte', note: 'Los estudiantes pueden elegir esta opción si todavía no están listos para Marcha Slave.' }, intermission: { section: 'Intermedio' }, secondHalf: { section: 'Segunda Parte', note: 'Los estudiantes de concierto completo continúan después del intermedio y preparan el programa completo.' } }, concertChoiceTitle: 'Elige Tu Nivel De Concierto', firstHalfPoints: 'Hasta 2.5 / 5 puntos de Orquesta', fullConcertPoints: 'Hasta 5 / 5 puntos de Orquesta', firstHalfBadge: 'Opción primera parte', fullConcertBadge: 'Opción concierto completo', arriveBy: 'Llegar a las 3:30 PM', finalsEyebrow: '7 de Agosto', finalsTitle: 'Día de Finales de Competencia', finalsText: 'Los competidores solo deben presentarse en Square Ebara a las 9:00 AM. El día incluye eliminatorias, descanso, clase magistral de Eddy Marcano, concierto de los 3 finalistas y anuncio del ganador en el lugar.', venueLabel: 'Lugar de Competencia', mustArrive: 'Los competidores deben llegar a las 9:00 AM.',
    finals: [
      { time: '9:00 AM', title: 'Llegada a Square Ebara', location: 'Square Ebara, 4-5-28 Ebara, Shinagawa City, Tokyo 142-0063', detail: 'Todos los competidores deben estar en el lugar de competencia a las 9:00 AM.' },
      { time: '9:00 AM-1:00 PM', title: 'Ronda eliminatoria', location: 'Square Ebara', detail: 'Presentaciones, evaluaciones, categorías de orquesta y ensamble, público y adjudicación.' },
      { time: '1:00 PM-2:30 PM', title: 'Descanso', location: 'Área de Square Ebara', detail: 'Almuerzo y preparación antes de la sesión finalista.' },
      { time: '2:45 PM-3:30 PM', title: 'Clase magistral + concierto de los 3 finalistas', location: 'Square Ebara', detail: 'Clase magistral por Eddy Marcano, presentaciones finalistas, comentarios y selección del ganador.' },
    ],
    applicationEyebrow: 'Aplicación', applyTitle: 'Inscríbete', applyText: 'La guía de repertorio se actualiza automáticamente después de seleccionar instrumento y edad.', success: 'Gracias. Tu aplicación fue enviada correctamente. El equipo de Simanof te enviará un correo con los próximos pasos.', submitAnother: 'Enviar otra', form: { firstName: 'Nombre', lastName: 'Apellido', email: 'Correo electrónico', age: 'Edad', instrument: 'Instrumento', chooseInstrument: 'Elige tu instrumento', concertMusic: 'Música orquestal del concierto', musicComing: 'La carpeta de música estará disponible pronto.', chosenPiece: 'Pieza de concierto elegida para la competencia', concertoMusicEmail: 'Antes de la competencia, envía la partitura de tu concierto a Information@simanof.org para que los jueces tengan la música.', rehearsalAttendance: '¿A cuál ensayo asistirás?', chooseRehearsal: 'Elige asistencia al ensayo', concertParticipation: 'Opción de participación en el concierto', chooseConcertParticipation: 'Elige opción de concierto', firstHalfLabel: 'Solo primera parte', fullConcertLabel: 'Concierto completo', firstHalfDescription: 'Tocar solo la primera parte. Esta opción es para estudiantes que pueden tocar la música inicial, pero todavía no están listos para Marcha Slave. Puede recibir hasta 2.5 de los 5 puntos de Orquesta.', fullConcertDescription: 'Tocar el concierto completo. Incluye la primera parte y toda la música después del intermedio, incluyendo Marcha Slave. Puede recibir hasta los 5 puntos de Orquesta.', checkbox: 'Entiendo que los participantes deben asistir al menos a un ensayo y a la opción del concierto del 6 de agosto seleccionada arriba, llegar al salón de concierto a las 3:30 PM y asistir a los eventos requeridos de la Gira Japón.', submit: 'Enviar Aplicación (Sin Costo)', submitting: 'Enviando...' }, rehearsalOptions: ['Solo ensayo del 4 de agosto', 'Solo ensayo del 5 de agosto', 'Ambos ensayos'],
  },
  ja: {
    languageLabel: '言語', home: 'ホーム', tourLabel: '日本ツアー', heroEyebrow: 'Simanof Japan 2026', heroTitle: 'グランド・コンペティション', heroText: '日本ツアーのコンペティションに申し込み、年齢別レパートリーを確認し、2026年日本公演につながるファイナリストの機会に備えましょう。', purposeEyebrow: 'コンペティションの目的', purposeTitle: '挑戦したい音楽家のために', purposeText: 'Simanof は、音楽家が新しい可能性を探り、成長し、国際的な舞台を経験できる機会をつくるために、このコンペティションを設立しました。特に、これまでコンペティションに参加したことがない学生や若い音楽家を対象にしています。', purposeCards: [{ title: '初めてでも参加しやすいコンペティション', text: 'このコンペティションは、初めて競う音楽家にも開かれています。真剣でありながら、支えとなり、刺激を受けられる経験を目指しています。' }, { title: 'オーケストラ演奏 + ソロ審査', text: '音楽家を本当に評価するには、ひとりでの演奏だけでなく、他の音楽家と一緒に演奏する姿も見る必要があると考えています。そのため総合点には、オーケストラ演奏とソロ・コンペティションの両方が含まれます。' }, { title: '協奏曲の演奏が必要', text: 'コンペティションに参加するには、任意の協奏曲を選べますが、ソロ曲は必ず協奏曲でなければなりません。' }, { title: 'ロンドン2027への道', text: '優勝者には$2,200 USDに加えて、Simanof London Tour 2027 へのサポートと、ソリストとして演奏する機会が与えられます。' }], judgesEyebrow: '審査員', judgesTitle: '審査員紹介', judgesText: '国際的な指揮、オーケストラ演奏、教育、Simanof Foundation のリーダーシップを備えた審査員が参加します。', judges: { 'Felipe Gomez Perez': { role: '音楽指揮者', highlights: ['オーケストラとオペラの分野で国際的に活動する指揮者・芸術リーダーです。', '詩的な表現力、構造の明確さ、生き生きとした指揮で知られています。', '文化交流と国際ツアーで豊富な経験を持っています。'] }, 'Eddy G. Marcano': { role: 'ヴァイオリニスト・音楽指揮者', highlights: ['ベネズエラを代表するヴァイオリニスト、指揮者、教育者、文化リーダーの一人です。', 'エル・システマとシモン・ボリバル交響楽団を通じて国際的なキャリアを築きました。', '芸術性、オーケストラでのリーダーシップ、若い音楽家への指導で知られています。'] }, 'Susan Siman': { role: 'Siman Orchestral Foundation 創設者・ディレクター', highlights: ['Siman Orchestral Foundation の創設者・ディレクターです。', '国際的な音楽教育、ツアー、奨学金、若い演奏家の舞台機会を率いています。', '若い芸術家にとっての規律、コミュニティ、音楽の変革力を大切にしています。'] } }, processEyebrow: '参加の流れ', processTitle: '申し込み、リハーサル、コンサート、コンペティション', processText: '流れはシンプルです。オンラインで申し込み、少なくとも1回リハーサルに参加し、8月6日のコンサートに出演し、8月7日にコンペティションへ参加します。', requiredOrder: '必要な順番', orderOne: 'オンラインで申し込み。', orderTwo: 'リハーサルに1回参加。', orderThree: 'コンサート、その後コンペティション。', stepLabel: 'ステップ',
    processSteps: [
      { step: '申し込み', date: '受付中', time: 'オンライン申込', title: 'コンペティション申込フォームを送信', venue: '楽器、演奏曲、参加するリハーサルを選択します。', detail: '申し込み後、自分の楽器のコンサート譜面をダウンロードし、コンサート曲とコンペティション曲の準備を始めてください。' },
      { step: 'リハーサル', date: '2026年8月4日または8月5日', time: '少なくとも1回参加', title: '必須リハーサルに参加', venue: '8月4日 J:COM浦安ホール、または8月5日 タワーホール船堀。', detail: '参加者は1回または両方のリハーサルに参加できます。8月6日のコンサートに向けた準備です。' },
      { step: 'コンサート', date: '2026年8月6日', time: '午後3:30までに到着', title: '日本ツアー・コンサートに出演', venue: 'コンサート会場の詳細は後日発表します。', detail: '決勝に参加する前に、コンサート出演が必要です。' },
      { step: 'コンペティション', date: '2026年8月7日', time: '午前9:00までに到着', title: 'コンペティション決勝日', venue: 'スクエア荏原（東京都品川区）。', detail: '午前9:00から午後1:00まで予選、その後マスタークラスとトップ3ファイナリストのコンサートを行います。' },
    ],
    rehearsalEyebrow: 'リハーサル会場', rehearsalTitle: 'リハーサル集合場所', rehearsalText: '申込フォームで少なくとも1つのリハーサルを選択してください。ご家族が到着を計画できるよう地図を掲載しています。', translatorNote: 'リハーサルでは、日本語を母語とする方のために日本語通訳がいます。', arrivalWindow: '到着時間', mapLocation: '地図', openMap: '地図を開く', musicEyebrow: 'コンサート譜面', musicTitle: 'コンサート譜面をダウンロード', musicText: 'すべての参加者は8月6日のコンサートにも出演します。下から楽器を選択し、Google Drive フォルダを開いて、自分のパート譜をリハーサルに持参してください。', musicHow: '譜面の入手方法', musicSteps: ['セレクターで楽器を選びます。', '該当する Google Drive フォルダを開きます。', 'リハーサル前にパート譜をダウンロードまたは印刷します。'], gradingEyebrow: '採点システム', gradingTitle: 'コンペティションの採点方法', gradingText: '参加者は5つのカテゴリーで評価されます。各カテゴリーは5点、合計25点です。', totalPoints: '合計点', pointsAbbrev: '点', gradingCategories: { orchestraConcert: { name: 'オーケストラ・コンサート', detail: '8月6日のコンサートでの参加姿勢、準備、舞台上での責任を評価します。前半のみの場合、この5点中最大2.5点までです。' }, time: { name: 'テンポ・リズム', detail: '安定した拍、リズムの正確さ、確実な入りを評価します。' }, technique: { name: 'テクニック', detail: 'コントロール、正確さ、アーティキュレーション、楽器の扱いを評価します。' }, soundQuality: { name: '音色・音質', detail: '音色、響き、バランス、音楽的な色彩を評価します。' }, phrasing: { name: 'フレージング', detail: '音楽の流れ、表現、強弱、スタイルを評価します。' } }, selectMusicInstrument: 'コンサート譜面の楽器を選択', availableFolder: '利用可能なフォルダ', availableFolders: '利用可能なフォルダ', openMusic: '開く', musicWord: '譜面', violinNote: 'ヴァイオリン奏者は Violin 1 または Violin 2 の担当を確認してください。不明な場合は先生と両方のフォルダを確認してください。', concertEyebrow: '8月6日 コンサート', piecesTitle: '演奏予定曲', programTitle: 'プログラム順', programText: '準備状況に合わせてコンサート参加を選んでください。前半のみは Marche Slave の準備がまだできていない学生向けですが、オーケストラ・コンサート項目は5点中2.5点までです。全曲参加は5点満点まで受けられます。', programSections: { firstHalf: { section: '前半', note: 'Marche Slave の準備がまだできていない学生は、この前半のみの参加を選べます。' }, intermission: { section: '休憩' }, secondHalf: { section: '後半', note: '全曲参加の学生は休憩後も続けて演奏し、プログラム全体を準備します。' } }, concertChoiceTitle: 'コンサート参加レベルを選択', firstHalfPoints: 'オーケストラ項目 最大2.5 / 5点', fullConcertPoints: 'オーケストラ項目 最大5 / 5点', firstHalfBadge: '前半オプション', fullConcertBadge: '全曲オプション', arriveBy: '午後3:30までに到着', finalsEyebrow: '8月7日', finalsTitle: 'コンペティション決勝日', finalsText: '参加者は午前9:00までにスクエア荏原へ集合してください。当日は予選、休憩、Eddy Marcano によるマスタークラス、トップ3ファイナリストのコンサート、会場での優勝者発表があります。', venueLabel: 'コンペティション会場', mustArrive: '参加者は午前9:00までに到着してください。',
    finals: [
      { time: '9:00 AM', title: 'スクエア荏原に到着', location: 'Square Ebara, 4-5-28 Ebara, Shinagawa City, Tokyo 142-0063', detail: '参加者は午前9:00までに会場へ集合してください。' },
      { time: '9:00 AM-1:00 PM', title: '予選', location: 'スクエア荏原', detail: '演奏、評価、オーケストラ・アンサンブル部門、審査を行います。' },
      { time: '1:00 PM-2:30 PM', title: '休憩', location: 'スクエア荏原周辺', detail: 'ファイナリスト・セッション前の昼食と準備時間です。' },
      { time: '2:45 PM-3:30 PM', title: 'マスタークラス + トップ3ファイナリスト・コンサート', location: 'スクエア荏原', detail: 'Eddy Marcano によるマスタークラス、ファイナリスト演奏、講評、優勝者選考を行います。' },
    ],
    applicationEyebrow: '申込', applyTitle: '申し込む', applyText: '楽器と年齢を選択すると、レパートリーガイドが自動で更新されます。', success: 'ありがとうございます。申込は正常に送信されました。Simanof チームより次の手順をメールでご連絡します。', submitAnother: 'もう一度送信', form: { firstName: '名', lastName: '姓', email: 'メール', age: '年齢', instrument: '楽器', chooseInstrument: '楽器を選択', concertMusic: 'コンサート用オーケストラ譜面', musicComing: 'コンサート譜面フォルダは近日公開予定です。', chosenPiece: 'コンペティション用の協奏曲', concertoMusicEmail: '審査員が楽譜を確認できるよう、コンペティション前に協奏曲の楽譜を Information@simanof.org へメールしてください。', rehearsalAttendance: '参加するリハーサル', chooseRehearsal: 'リハーサル参加を選択', concertParticipation: 'コンサート参加オプション', chooseConcertParticipation: 'コンサート参加を選択', firstHalfLabel: '前半のみ', fullConcertLabel: '全曲', firstHalfDescription: '前半のみ演奏します。Marche Slave の準備がまだできていない学生向けです。この選択はオーケストラ・コンサート項目で5点中2.5点までです。', fullConcertDescription: '全曲を演奏します。前半と休憩後のすべての曲、Marche Slave を含みます。この選択はオーケストラ・コンサート項目で5点満点まで受けられます。', checkbox: 'コンペティション参加者は少なくとも1回のリハーサルと上で選択した8月6日のコンサートに参加し、午後3:30までに到着し、日本ツアー関連の必須イベントに参加することを理解しています。', submit: '申込を送信（無料）', submitting: '送信中...' }, rehearsalOptions: ['8月4日のリハーサルのみ', '8月5日のリハーサルのみ', '両方のリハーサル'],
  },
};

function FloatingPetals() {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) return null;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {Array.from({ length: 18 }).map((_, index) => (
        <motion.img
          key={index}
          src={Cherry}
          alt=""
          className="absolute w-5 h-5 opacity-70"
          style={{ left: `${(index * 17) % 100}%` }}
          initial={{ y: '-10vh', rotate: index * 18 }}
          animate={{ y: '115vh', x: index % 2 ? 34 : -28, rotate: 360 + index * 18 }}
          transition={{ duration: 22 + (index % 5) * 4, repeat: Infinity, delay: index * 0.7, ease: 'linear' }}
        />
      ))}
    </div>
  );
}

export default function CompetitionPage() {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [musicInstrument, setMusicInstrument] = useState('Violin');
  const [language, setLanguage] = useState('en');
  const copy = COPY[language];

  const repGroup = useMemo(() => {
    if (!form.instrument || !form.age) return null;
    return getRepertoireGroup(form.instrument, form.age);
  }, [form.instrument, form.age]);

  const musicLinks = useMemo(() => MUSIC_FOLDERS[form.instrument] || [], [form.instrument]);
  const selectedMusicLinks = useMemo(() => MUSIC_FOLDERS[musicInstrument] || [], [musicInstrument]);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const validate = () => {
    if (!form.firstName.trim() || !form.lastName.trim()) return 'Name is required.';
    if (!form.email.trim()) return 'Email is required.';
    if (!form.age || Number.isNaN(Number(form.age))) return 'Please enter a valid age.';
    if (!form.instrument) return 'Please select your instrument.';
    if (repGroup?.tooYoung) return `You must be at least ${repGroup.minAge} years old to compete on ${form.instrument}.`;
    if (REPERTOIRE[form.instrument] && !repGroup) return 'We could not match your age to a repertoire group. Please check your age.';
    if (!form.chosenPiece.trim()) return 'Please enter your chosen concerto piece for the competition.';
    if (!form.rehearsalAttendance) return 'Please choose which rehearsal you will attend.';
    if (!form.concertParticipation) return 'Please choose whether you will play the first half or the full concert.';
    if (!form.willAttend) return 'Please confirm you can attend required rehearsals, concerts, and competition events.';
    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validate();

    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      const normalizedEmail = form.email.trim().toLowerCase();
      const existingApplication = query(
        collection(db, COLLECTION_NAME),
        where('email', '==', normalizedEmail),
        where('competition', '==', COMPETITION_NAME),
        limit(1)
      );
      const snapshot = await getDocs(existingApplication);

      if (!snapshot.empty) {
        setError('It looks like this email already submitted an application for the Japan competition.');
        setSubmitting(false);
        return;
      }

      await addDoc(collection(db, COLLECTION_NAME), {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: normalizedEmail,
        age: Number(form.age),
        instrument: form.instrument,
        chosenPiece: form.chosenPiece.trim(),
        rehearsalAttendance: form.rehearsalAttendance,
        concertParticipation: form.concertParticipation,
        ageGroupTitle: repGroup?.title || null,
        willAttend: form.willAttend,
        competition: COMPETITION_NAME,
        fee: 0,
        source: 'japan-tour-public-competition',
        submittedAt: serverTimestamp(),
      });

      setSent(true);
      setForm(initialForm);
    } catch (submitError) {
      console.error('[Competition] Submit error:', submitError);
      setError('Something went wrong submitting your application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#f8f3ea] text-black">
      <img src={Sky} alt="" className="absolute inset-x-0 top-0 h-[620px] w-full object-cover opacity-70" />
      <img src={Mountain} alt="" className="absolute inset-x-0 top-24 h-[520px] w-full object-cover opacity-45" />
      <FloatingPetals />

      <nav className="relative z-20 border-b border-black/10 bg-white/55 backdrop-blur-md">
        <div className="container mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="inline-flex items-center gap-3 font-semibold text-black">
            <img src={SmallLogo} alt="Simanof" className="h-10 w-auto" />
            <span className="hidden sm:inline">{copy.tourLabel}</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 rounded border border-black px-4 py-2 text-sm font-medium transition hover:bg-red-600 hover:text-white">
            <FaArrowLeft className="h-3 w-3" />
            {copy.home}
          </Link>
        </div>
      </nav>

      <section className="relative z-10 px-6 pb-16 pt-16 md:pt-24">
        <div className="mx-auto max-w-6xl text-center">
          <div className="mb-8 flex flex-col items-center gap-3">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-red-700">Language / Idioma / 言語</p>
            <div className="inline-flex flex-wrap justify-center gap-2 rounded-full border border-red-100 bg-white/75 p-2 shadow-lg backdrop-blur">
              {LANGUAGE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={language === option.value}
                  onClick={() => setLanguage(option.value)}
                  className={`rounded-full px-5 py-2 text-sm font-bold transition ${language === option.value ? 'bg-red-600 text-white shadow-md' : 'bg-white text-red-800 hover:bg-red-50'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <p className="text-sm font-bold uppercase tracking-[0.28em] text-red-700">{copy.heroEyebrow}</p>
          <h1 className="mt-4 font-serif text-5xl font-bold leading-tight md:text-7xl">{copy.heroTitle}</h1>
          <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-gray-800 md:text-xl">
            {copy.heroText}
          </p>
          <div className="mx-auto mt-10 max-w-6xl rounded-lg border border-red-100 bg-white/90 p-5 text-left shadow-2xl backdrop-blur md:p-6">
            <div className="mx-auto max-w-4xl text-center">
              <p className="inline-flex rounded-full bg-red-600 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-white">{copy.purposeEyebrow}</p>
              <h2 className="mt-4 font-serif text-3xl font-bold leading-tight md:text-5xl">{copy.purposeTitle}</h2>
              <p className="mx-auto mt-4 max-w-3xl text-sm leading-7 text-gray-700 md:text-base">{copy.purposeText}</p>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {copy.purposeCards.map((card, index) => {
                const Symbol = PURPOSE_SYMBOLS[index] || FaCompass;

                return (
                  <article key={card.title} className="rounded-lg border border-red-100 bg-[#fffaf4] p-5 shadow-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-white text-red-700 shadow-sm">
                        <Symbol className="h-5 w-5" />
                      </span>
                      <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-bold text-red-700">{index + 1}</span>
                    </div>
                    <h3 className="mt-4 font-serif text-xl font-bold leading-tight text-gray-950">{card.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-gray-600">{card.text}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mx-auto mt-12 grid max-w-5xl grid-cols-1 items-end gap-5 md:grid-cols-3">
          {PRIZES.map((prize) => (
            <div
              key={prize.place}
              className={`rounded-lg border-t-4 ${prize.accent} bg-white/80 p-6 text-center shadow-xl backdrop-blur ${prize.featured ? 'md:-mt-6 md:p-8' : ''}`}
            >
              <p className="font-serif text-2xl font-bold text-red-700">{prize.place}</p>
              <p className="mt-3 text-4xl font-extrabold">{prize.prize}</p>
              <p className="mt-2 text-sm font-medium text-gray-700">{prize.detail}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="relative z-10 bg-[#fffaf4]/95 px-6 py-16 backdrop-blur">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-bold uppercase tracking-[0.28em] text-red-700">{copy.judgesEyebrow}</p>
            <h2 className="mt-3 font-serif text-4xl font-bold md:text-5xl">{copy.judgesTitle}</h2>
            <p className="mt-4 text-gray-700">
              {copy.judgesText}
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
            {JUDGES.map((judge) => {
              const localizedJudge = copy.judges[judge.name] || judge;

              return (
                <article key={judge.name} className="overflow-hidden rounded-lg border border-red-100 bg-white shadow-xl">
                  <div className="relative h-72 bg-red-50">
                    <img src={judge.image} alt={judge.name} className={`h-full w-full object-cover ${judge.imageClass || 'object-center'}`} />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-5 text-white">
                      <h3 className="font-serif text-2xl font-bold">{judge.name}</h3>
                      <p className="text-sm font-medium text-white/85">{localizedJudge.role}</p>
                    </div>
                  </div>
                  <div className="space-y-3 p-5">
                    {localizedJudge.highlights.map((highlight) => (
                      <p key={highlight} className="border-l-2 border-red-500 pl-3 text-sm leading-6 text-gray-700">
                        {highlight}
                      </p>
                    ))}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative z-10 bg-white/95 px-6 py-16 backdrop-blur">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-red-700">{copy.gradingEyebrow}</p>
              <h2 className="mt-3 font-serif text-4xl font-bold md:text-5xl">{copy.gradingTitle}</h2>
              <p className="mt-4 text-gray-700">{copy.gradingText}</p>
              <div className="mt-6 inline-flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-red-950">
                <span className="text-sm font-bold uppercase tracking-[0.18em] text-red-700">{copy.totalPoints}</span>
                <span className="font-serif text-4xl font-bold">25</span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {GRADING_CATEGORIES.map((category) => {
                const localizedCategory = copy.gradingCategories[category.id];

                return (
                  <article key={category.id} className="rounded-lg border border-red-100 bg-[#fffaf4] p-5 shadow-sm">
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="font-serif text-xl font-bold leading-snug">{localizedCategory.name}</h3>
                      <span className="rounded bg-red-600 px-3 py-1 text-sm font-bold text-white">{category.points} {copy.pointsAbbrev}</span>
                    </div>
                    <p className="mt-3 text-sm leading-6 text-gray-600">{localizedCategory.detail}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 bg-white/95 px-6 py-16 backdrop-blur">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-red-700">{copy.processEyebrow}</p>
              <h2 className="mt-3 font-serif text-4xl font-bold md:text-5xl">{copy.processTitle}</h2>
              <p className="mt-4 text-gray-700">
                {copy.processText}
              </p>
            </div>

            <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-red-950 shadow-lg">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-red-700">{copy.requiredOrder}</p>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded bg-white p-4 shadow-sm">
                  <p className="text-2xl font-bold">1</p>
                  <p className="text-sm text-gray-700">{copy.orderOne}</p>
                </div>
                <div className="rounded bg-white p-4 shadow-sm">
                  <p className="text-2xl font-bold">2</p>
                  <p className="text-sm text-gray-700">{copy.orderTwo}</p>
                </div>
                <div className="rounded bg-white p-4 shadow-sm">
                  <p className="text-2xl font-bold">3-4</p>
                  <p className="text-sm text-gray-700">{copy.orderThree}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {copy.processSteps.map((item, index) => (
              <article key={item.title} className="rounded-lg border border-red-100 bg-[#fffaf4] p-5 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-widest text-red-700">{copy.stepLabel} {index + 1}</p>
                <p className="mt-3 font-serif text-2xl font-bold">{item.step}</p>
                <p className="mt-2 text-sm font-bold text-red-700">{item.date}</p>
                <p className="mt-1 text-sm font-semibold text-gray-800">{item.time}</p>
                <p className="mt-4 font-semibold">{item.title}</p>
                <p className="mt-1 text-sm text-gray-600">{item.venue}</p>
                {item.detail && <p className="mt-3 text-xs font-medium leading-5 text-gray-500">{item.detail}</p>}
              </article>
            ))}
          </div>

          <div className="mt-12">
            <div className="max-w-3xl">
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-red-700">{copy.rehearsalEyebrow}</p>
              <h3 className="mt-2 font-serif text-3xl font-bold">{copy.rehearsalTitle}</h3>
              <p className="mt-3 text-sm leading-6 text-gray-700">{copy.rehearsalText}</p>
              <p className="mt-3 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800">{copy.translatorNote}</p>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
              {REHEARSALS.map((rehearsal) => (
                <article key={rehearsal.id} className="overflow-hidden rounded-lg border border-red-100 bg-white shadow-lg">
                  <div className="p-5">
                    <p className="text-xs font-bold uppercase tracking-widest text-red-700">{rehearsal.date}</p>
                    <h4 className="mt-2 font-serif text-2xl font-bold">{rehearsal.venue}</h4>
                    <p className="mt-1 text-sm font-semibold text-red-700">{copy.arrivalWindow}: {rehearsal.time}</p>
                    <p className="mt-2 text-sm text-gray-600">{rehearsal.address}</p>
                  </div>
                  <div className="border-t border-red-100 bg-gradient-to-br from-red-50 via-white to-sky-50 p-5">
                    <div className="flex min-h-52 flex-col justify-between rounded-lg border border-red-100 bg-white/85 p-5 shadow-inner">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-widest text-red-700">{copy.mapLocation}</p>
                        <p className="mt-3 font-serif text-xl font-bold">{rehearsal.venue}</p>
                        <p className="mt-2 text-sm leading-6 text-gray-600">{rehearsal.address}</p>
                      </div>
                      <a
                        href={rehearsal.mapUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-5 inline-flex w-fit items-center gap-2 rounded bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
                      >
                        {copy.openMap}
                        <FaExternalLinkAlt className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 bg-white px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-red-700">{copy.musicEyebrow}</p>
              <h2 className="mt-3 font-serif text-4xl font-bold md:text-5xl">{copy.musicTitle}</h2>
              <p className="mt-4 text-gray-700">
                {copy.musicText}
              </p>
            </div>

            <div className="rounded-lg border border-red-200 bg-[#fff7f1] p-5 shadow-lg">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-700">{copy.musicHow}</p>
              <ol className="mt-4 space-y-3 text-sm leading-6 text-gray-700">
                <li className="flex gap-3"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white">1</span><span>{copy.musicSteps[0]}</span></li>
                <li className="flex gap-3"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white">2</span><span>{copy.musicSteps[1]}</span></li>
                <li className="flex gap-3"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white">3</span><span>{copy.musicSteps[2]}</span></li>
              </ol>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-[0.85fr_1.15fr]">
            <aside className="rounded-lg border border-red-100 bg-[#fffaf4] p-5 shadow-sm">
              <label className="block text-sm font-semibold text-gray-800">
                {copy.selectMusicInstrument}
                <select
                  className="mt-2 w-full rounded-lg border border-red-200 bg-white px-3 py-3 text-base font-semibold text-gray-900 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100"
                  value={musicInstrument}
                  onChange={(event) => setMusicInstrument(event.target.value)}
                >
                  {Object.keys(MUSIC_FOLDERS).sort().map((instrument) => <option key={instrument} value={instrument}>{instrument}</option>)}
                </select>
              </label>

              <div className="mt-5 rounded-lg border border-red-100 bg-white p-4">
                <p className="text-xs font-bold uppercase tracking-widest text-red-700">{selectedMusicLinks.length > 1 ? copy.availableFolders : copy.availableFolder}</p>
                <h3 className="mt-2 font-serif text-2xl font-bold">{musicInstrument}</h3>
                <div className="mt-4 grid grid-cols-1 gap-3">
                  {selectedMusicLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-600 px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-red-700"
                    >
                      <span>{copy.openMusic} {link.label} {copy.musicWord}</span>
                      <FaExternalLinkAlt className="h-3 w-3 shrink-0" />
                    </a>
                  ))}
                </div>
                {musicInstrument === 'Violin' && <p className="mt-3 text-xs leading-5 text-gray-600">{copy.violinNote}</p>}
              </div>
            </aside>

            <div className="rounded-lg border border-red-100 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-2 border-b border-red-100 pb-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-700">{copy.concertEyebrow}</p>
                  <h3 className="mt-2 font-serif text-3xl font-bold">{copy.programTitle}</h3>
                </div>
                <p className="text-sm font-semibold text-red-700">{copy.arriveBy}</p>
              </div>
              <p className="mt-4 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 text-sm font-semibold leading-6 text-sky-900">{copy.programText}</p>

              <div className="mt-5 rounded-lg border border-red-100 bg-[#fffaf4] p-4">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-700">{copy.concertChoiceTitle}</p>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <article className="rounded-lg border border-red-200 bg-white p-4 shadow-sm">
                    <p className="font-serif text-2xl font-bold">{copy.form.firstHalfLabel}</p>
                    <p className="mt-2 rounded bg-red-50 px-3 py-2 text-sm font-bold text-red-800">{copy.firstHalfPoints}</p>
                    <p className="mt-3 text-sm leading-6 text-gray-600">{copy.form.firstHalfDescription}</p>
                  </article>
                  <article className="rounded-lg border border-emerald-200 bg-white p-4 shadow-sm">
                    <p className="font-serif text-2xl font-bold">{copy.form.fullConcertLabel}</p>
                    <p className="mt-2 rounded bg-emerald-50 px-3 py-2 text-sm font-bold text-emerald-800">{copy.fullConcertPoints}</p>
                    <p className="mt-3 text-sm leading-6 text-gray-600">{copy.form.fullConcertDescription}</p>
                  </article>
                </div>
              </div>

              <div className="mt-5 space-y-5">
                {CONCERT_PROGRAM.map((section) => {
                  const localizedSection = copy.programSections[section.id];

                  return section.intermission ? (
                    <div key={section.id} className="flex items-center gap-4">
                      <div className="h-px flex-1 bg-red-100" />
                      <p className="rounded-full border border-red-200 bg-white px-5 py-2 text-sm font-bold uppercase tracking-[0.18em] text-red-700">{localizedSection.section}</p>
                      <div className="h-px flex-1 bg-red-100" />
                    </div>
                  ) : (
                    <section key={section.id} className="rounded-lg border border-red-100 bg-[#fffaf4] p-4">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="font-serif text-2xl font-bold">{localizedSection.section}</p>
                          <p className="mt-1 text-xs font-semibold leading-5 text-gray-600">{localizedSection.note}</p>
                        </div>
                        <span className="w-fit rounded bg-red-600 px-3 py-1 text-xs font-bold uppercase tracking-widest text-white">
                          {section.id === 'firstHalf' ? copy.firstHalfBadge : copy.fullConcertBadge}
                        </span>
                      </div>

                      <div className="mt-4 grid grid-cols-1 gap-3">
                        {section.pieces.map((piece, index) => (
                          <article key={piece.title} className="rounded-lg border border-gray-100 bg-white p-4">
                            <div className="flex gap-3">
                              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-50 text-xs font-bold text-red-700">{index + 1}</span>
                              <div>
                                <h4 className="font-serif text-lg font-bold leading-snug">{piece.title}</h4>
                                <p className="mt-1 text-sm text-gray-600">{piece.composer}</p>
                                <p className="mt-1 text-xs font-semibold text-red-700">{piece.leader}</p>
                              </div>
                            </div>
                          </article>
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 bg-[#fffaf4] px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-end">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-red-700">{copy.finalsEyebrow}</p>
              <h2 className="mt-3 font-serif text-4xl font-bold md:text-5xl">{copy.finalsTitle}</h2>
              <p className="mt-4 text-gray-700">
                {copy.finalsText}
              </p>
            </div>

            <div className="rounded-lg border border-red-200 bg-white p-5 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-widest text-red-700">{copy.venueLabel}</p>
              <p className="mt-2 font-semibold">Square Ebara</p>
              <p className="mt-1 text-sm text-gray-600">4-5-28 Ebara, Shinagawa City, Tokyo 142-0063</p>
              <p className="mt-4 rounded bg-red-50 px-3 py-2 text-sm font-semibold text-red-800">{copy.mustArrive}</p>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {copy.finals.map((item) => (
              <article key={item.title} className="rounded-lg border border-red-100 bg-white p-5 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-widest text-red-700">{item.time}</p>
                <h3 className="mt-2 font-serif text-xl font-bold">{item.title}</h3>
                <p className="mt-2 text-sm text-gray-600">{item.location}</p>
                {item.detail && <p className="mt-3 text-xs font-medium leading-5 text-gray-500">{item.detail}</p>}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="relative z-10 bg-white px-6 py-16">
        <div className="mx-auto max-w-4xl">
          <section className="rounded-lg border border-black/10 bg-white p-5 shadow-2xl md:p-8">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-700">{copy.applicationEyebrow}</p>
                <h2 className="mt-2 font-serif text-3xl font-bold">{copy.applyTitle}</h2>
                <p className="mt-2 text-sm text-gray-600">{copy.applyText}</p>
              </div>
              <GiPianoKeys className="hidden h-10 w-10 text-red-700 sm:block" />
            </div>

            {sent && (
              <div className="mb-5 rounded-lg border border-green-200 bg-green-50 p-4 text-green-800">
                {copy.success}
                <button type="button" onClick={() => setSent(false)} className="ml-2 font-semibold underline">{copy.submitAnother}</button>
              </div>
            )}

            <form onSubmit={handleSubmit} className={`space-y-5 ${sent ? 'opacity-45 pointer-events-none' : ''}`} aria-busy={submitting}>
              {error && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="block text-sm font-medium text-gray-700">
                  {copy.form.firstName}
                  <input className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100" value={form.firstName} onChange={(event) => update('firstName', event.target.value)} autoComplete="given-name" required />
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  {copy.form.lastName}
                  <input className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100" value={form.lastName} onChange={(event) => update('lastName', event.target.value)} autoComplete="family-name" required />
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  {copy.form.email}
                  <input type="email" className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100" value={form.email} onChange={(event) => update('email', event.target.value)} autoComplete="email" required />
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  {copy.form.age}
                  <input type="number" min="1" className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100" value={form.age} onChange={(event) => update('age', event.target.value)} required />
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  {copy.form.instrument}
                  <select className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100" value={form.instrument} onChange={(event) => update('instrument', event.target.value)} required>
                    <option value="">{copy.form.chooseInstrument}</option>
                    {ALL_INSTRUMENTS.map((instrument) => <option key={instrument} value={instrument}>{instrument}</option>)}
                  </select>
                </label>
              </div>

              {form.instrument && form.age && repGroup?.tooYoung && (
                <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-yellow-800">
                  You must be at least {repGroup.minAge} years old to compete on {form.instrument}.
                </div>
              )}

              {form.instrument && (
                <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4">
                  <p className="font-semibold text-emerald-900">{copy.form.concertMusic}</p>
                  {musicLinks.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {musicLinks.map((link) => (
                        <a key={link.label} href={link.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded border border-emerald-200 bg-white px-3 py-2 text-sm font-medium text-emerald-800 transition hover:border-emerald-400">
                          {link.label}
                          <FaExternalLinkAlt className="h-3 w-3" />
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-1 text-sm text-emerald-800">{copy.form.musicComing}</p>
                  )}
                </div>
              )}

              <label className="block text-sm font-medium text-gray-700">
                {copy.form.chosenPiece}
                <input className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100" value={form.chosenPiece} onChange={(event) => update('chosenPiece', event.target.value)} required />
                <span className="mt-1 block text-xs font-medium leading-5 text-gray-500">{copy.purposeCards[2].text}</span>
                <span className="mt-1 block rounded border border-red-100 bg-red-50 px-3 py-2 text-xs font-semibold leading-5 text-red-800">{copy.form.concertoMusicEmail}</span>
              </label>

              <label className="block text-sm font-medium text-gray-700">
                {copy.form.rehearsalAttendance}
                <select className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100" value={form.rehearsalAttendance} onChange={(event) => update('rehearsalAttendance', event.target.value)} required>
                  <option value="">{copy.form.chooseRehearsal}</option>
                  {REHEARSAL_OPTIONS.map((option, index) => <option key={option.value} value={option.value}>{copy.rehearsalOptions[index]}</option>)}
                </select>
              </label>

              <fieldset className="rounded-lg border border-red-100 bg-[#fffaf4] p-4">
                <legend className="px-1 text-sm font-semibold text-gray-800">{copy.form.concertParticipation}</legend>
                <p className="mt-1 text-xs leading-5 text-gray-600">{copy.programText}</p>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {CONCERT_PARTICIPATION_OPTIONS.map((option) => {
                    const checked = form.concertParticipation === option.value;
                    const description = option.value === 'First half only' ? copy.form.firstHalfDescription : copy.form.fullConcertDescription;
                    const points = option.value === 'First half only' ? copy.firstHalfPoints : copy.fullConcertPoints;

                    return (
                      <label
                        key={option.value}
                        className={`flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition ${checked ? 'border-red-500 bg-white shadow-sm' : 'border-red-100 bg-white/70 hover:border-red-300'}`}
                      >
                        <input
                          type="radio"
                          name="concertParticipation"
                          value={option.value}
                          checked={checked}
                          onChange={(event) => update('concertParticipation', event.target.value)}
                          className="mt-1 h-4 w-4 accent-red-600"
                          required
                        />
                        <span>
                          <span className="block font-semibold text-gray-900">{option.value === 'First half only' ? copy.form.firstHalfLabel : copy.form.fullConcertLabel}</span>
                          <span className={`mt-2 inline-flex rounded px-2 py-1 text-xs font-bold ${option.value === 'First half only' ? 'bg-red-50 text-red-800' : 'bg-emerald-50 text-emerald-800'}`}>{points}</span>
                          <span className="mt-1 block text-xs leading-5 text-gray-600">{description}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              <label className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
                <input type="checkbox" checked={form.willAttend} onChange={(event) => update('willAttend', event.target.checked)} className="mt-1 h-4 w-4 accent-red-600" required />
                <span>{copy.form.checkbox}</span>
              </label>

              <button type="submit" disabled={submitting} className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-6 py-3 font-semibold text-white shadow-lg transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60">
                {submitting && <FaSpinner className="h-4 w-4 animate-spin" />}
                {submitting ? copy.form.submitting : copy.form.submit}
              </button>
            </form>
          </section>
        </div>
      </section>
    </main>
  );
}
